import { QueryClient } from "@tanstack/react-query";
import { createRouter, type RouterHistory } from "@tanstack/react-router";

import { PageLoader } from "../components/ui/spinner";
import { routeTree } from "../routeTree.gen";
import { ApiError } from "./api";

/**
 * Factories rather than singletons: the server renders pages for many visitors at once,
 * and a shared cache would hand one visitor's data to the next. The browser calls each
 * once.
 */
export const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // A 4xx is the server's final answer; retrying only delays the error the user
          // needs to see. Retry transient failures only.
          if (error instanceof ApiError && error.status < 500) return false;
          return failureCount < 2;
        },
      },
    },
  });

/** The server passes a memory history for the requested URL; the browser uses its own. */
export const createAppRouter = (queryClient: QueryClient, history?: RouterHistory) =>
  createRouter({
    routeTree,
    history,
    context: { queryClient },
    defaultPreload: "intent",
    // Loaders fetch before the page renders, so a page appears whole instead of text first
    // and content after. A fast load keeps the previous page on screen; a slow one shows
    // the loader, held long enough (pendingMinMs, 500ms default) not to flash.
    defaultPendingComponent: PageLoader,
    defaultPendingMs: 300,
  });

/**
 * Registers the router's type globally, so `useSearch`, `Link` and the rest infer routes
 * and their params instead of falling back to `any`.
 */
declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }

  /** Routes rendered on the server opt in: see entry-server.tsx. */
  interface StaticDataRouteOption {
    ssr?: boolean;
  }
}
